import type { SerializedNode, ConversionContext } from '../types';
import { getAttribute, getAllChildElements } from '../parser';
import { parseBoolean } from '../utils';
import {
  FORM_TEXT_FIELD_DEFAULTS,
  FORM_EMAIL_FIELD_DEFAULTS,
  FORM_PHONE_FIELD_DEFAULTS,
  FORM_DROPDOWN_FIELD_DEFAULTS,
  FORM_SELECTION_FIELD_DEFAULTS,
  FORM_CONSENT_FIELD_DEFAULTS,
  FORM_SUBMIT_BUTTON_DEFAULTS,
} from '../utils/defaults';
import {
  FORM_TEXT_FIELD_NODE_NAME,
  FORM_EMAIL_FIELD_NODE_NAME,
  FORM_PHONE_FIELD_NODE_NAME,
  FORM_DROPDOWN_FIELD_NODE_NAME,
  FORM_SELECTION_FIELD_NODE_NAME,
  FORM_CONSENT_FIELD_NODE_NAME,
  FORM_SUBMIT_BUTTON_NODE_NAME,
} from '../../utils/constants';

/**
 * Transform a <shortText> XML element into a CraftJS FormTextField node
 *
 * @param element - The XML element
 * @param context - Conversion context
 * @returns SerializedNode for the FormTextField
 */
export const transformShortText = (
  element: Element,
  context: ConversionContext,
): SerializedNode => {
  const labelAttr = getAttribute(element, 'label');
  const label = labelAttr || FORM_TEXT_FIELD_DEFAULTS.label;

  const placeholderAttr = getAttribute(element, 'placeholder');
  const placeholder = placeholderAttr || FORM_TEXT_FIELD_DEFAULTS.placeholder;

  const requiredAttr = getAttribute(element, 'required');
  const required = parseBoolean(requiredAttr, FORM_TEXT_FIELD_DEFAULTS.required);

  const helperTextAttr = getAttribute(element, 'helperText');
  const helpText = helperTextAttr || FORM_TEXT_FIELD_DEFAULTS.helpText;
  const showHelpText = helperTextAttr ? true : FORM_TEXT_FIELD_DEFAULTS.showHelpText;

  const showLabelAttr = getAttribute(element, 'showLabel');
  const showLabel = parseBoolean(showLabelAttr, FORM_TEXT_FIELD_DEFAULTS.showLabel);

  const props: Record<string, unknown> = {
    id: FORM_TEXT_FIELD_DEFAULTS.id,
    inputType: FORM_TEXT_FIELD_DEFAULTS.inputType,
    label,
    showLabel,
    placeholder,
    required,
    hidden: FORM_TEXT_FIELD_DEFAULTS.hidden,
    helpText,
    showHelpText,
    enforceCharacterLimit: FORM_TEXT_FIELD_DEFAULTS.enforceCharacterLimit,
    minCharacterLimit: FORM_TEXT_FIELD_DEFAULTS.minCharacterLimit,
    maxCharacterLimit: FORM_TEXT_FIELD_DEFAULTS.maxCharacterLimit,
    urlMapping: FORM_TEXT_FIELD_DEFAULTS.urlMapping,
  };

  return {
    type: { resolvedName: FORM_TEXT_FIELD_NODE_NAME.replace(/\s/g, '') },
    isCanvas: false,
    props,
    displayName: FORM_TEXT_FIELD_NODE_NAME,
    custom: {
      displayName: 'Text',
      isLocked: false,
    },
    hidden: false,
    nodes: [],
    linkedNodes: {},
    parent: context.parentId,
  };
};

/**
 * Transform an <email> XML element into a CraftJS FormEmailField node
 *
 * @param element - The XML element
 * @param context - Conversion context
 * @returns SerializedNode for the FormEmailField
 */
export const transformEmail = (element: Element, context: ConversionContext): SerializedNode => {
  const labelAttr = getAttribute(element, 'label');
  const label = labelAttr || FORM_EMAIL_FIELD_DEFAULTS.label;

  const placeholderAttr = getAttribute(element, 'placeholder');
  const placeholder = placeholderAttr || FORM_EMAIL_FIELD_DEFAULTS.placeholder;

  const requiredAttr = getAttribute(element, 'required');
  const required = parseBoolean(requiredAttr, FORM_EMAIL_FIELD_DEFAULTS.required);

  const helperTextAttr = getAttribute(element, 'helperText');
  const helpText = helperTextAttr || FORM_EMAIL_FIELD_DEFAULTS.helpText;
  const showHelpText = helperTextAttr ? true : FORM_EMAIL_FIELD_DEFAULTS.showHelpText;

  const showLabelAttr = getAttribute(element, 'showLabel');
  const showLabel = parseBoolean(showLabelAttr, FORM_EMAIL_FIELD_DEFAULTS.showLabel);

  const props: Record<string, unknown> = {
    id: FORM_EMAIL_FIELD_DEFAULTS.id,
    inputType: FORM_EMAIL_FIELD_DEFAULTS.inputType,
    label,
    showLabel,
    placeholder,
    required,
    hidden: FORM_EMAIL_FIELD_DEFAULTS.hidden,
    helpText,
    showHelpText,
    enforceCharacterLimit: FORM_EMAIL_FIELD_DEFAULTS.enforceCharacterLimit,
    minCharacterLimit: FORM_EMAIL_FIELD_DEFAULTS.minCharacterLimit,
    maxCharacterLimit: FORM_EMAIL_FIELD_DEFAULTS.maxCharacterLimit,
    urlMapping: FORM_EMAIL_FIELD_DEFAULTS.urlMapping,
    primaryEmailField: FORM_EMAIL_FIELD_DEFAULTS.primaryEmailField,
  };

  return {
    type: { resolvedName: FORM_EMAIL_FIELD_NODE_NAME.replace(/\s/g, '') },
    isCanvas: false,
    props,
    displayName: FORM_EMAIL_FIELD_NODE_NAME,
    custom: {
      displayName: 'Email',
      isLocked: false,
    },
    hidden: false,
    nodes: [],
    linkedNodes: {},
    parent: context.parentId,
  };
};

/**
 * Transform a <phone> XML element into a CraftJS FormPhoneField node
 *
 * @param element - The XML element
 * @param context - Conversion context
 * @returns SerializedNode for the FormPhoneField
 */
export const transformPhone = (element: Element, context: ConversionContext): SerializedNode => {
  const labelAttr = getAttribute(element, 'label');
  const label = labelAttr || FORM_PHONE_FIELD_DEFAULTS.label;

  const placeholderAttr = getAttribute(element, 'placeholder');
  const placeholder = placeholderAttr || FORM_PHONE_FIELD_DEFAULTS.placeholder;

  const requiredAttr = getAttribute(element, 'required');
  const required = parseBoolean(requiredAttr, FORM_PHONE_FIELD_DEFAULTS.required);

  const helperTextAttr = getAttribute(element, 'helperText');
  const helpText = helperTextAttr || FORM_PHONE_FIELD_DEFAULTS.helpText;
  const showHelpText = helperTextAttr ? true : FORM_PHONE_FIELD_DEFAULTS.showHelpText;

  const showLabelAttr = getAttribute(element, 'showLabel');
  const showLabel = parseBoolean(showLabelAttr, FORM_PHONE_FIELD_DEFAULTS.showLabel);

  const props: Record<string, unknown> = {
    id: FORM_PHONE_FIELD_DEFAULTS.id,
    label,
    showLabel,
    placeholder,
    required,
    hidden: FORM_PHONE_FIELD_DEFAULTS.hidden,
    helpText,
    showHelpText,
  };

  return {
    type: { resolvedName: FORM_PHONE_FIELD_NODE_NAME.replace(/\s/g, '') },
    isCanvas: false,
    props,
    displayName: FORM_PHONE_FIELD_NODE_NAME,
    custom: {
      displayName: 'Phone Field',
      isLocked: false,
    },
    hidden: false,
    nodes: [],
    linkedNodes: {},
    parent: context.parentId,
  };
};

/**
 * Parse <option> children from a dropdown or select element
 *
 * @param element - The parent XML element containing <option> children
 * @returns Array of option strings
 */
const parseOptions = (element: Element): string[] => {
  const children = getAllChildElements(element);
  const options: string[] = [];

  children.forEach((child) => {
    if (child.tagName.toLowerCase() === 'option') {
      // Get text content from the option element
      const text = child.textContent?.trim() || '';
      if (text) {
        options.push(text);
      }
    }
  });

  return options.length > 0 ? options : FORM_DROPDOWN_FIELD_DEFAULTS.options;
};

/**
 * Transform a <dropdown> XML element into a CraftJS FormDropdownField node
 *
 * @param element - The XML element
 * @param context - Conversion context
 * @returns SerializedNode for the FormDropdownField
 */
export const transformDropdown = (element: Element, context: ConversionContext): SerializedNode => {
  const labelAttr = getAttribute(element, 'label');
  const label = labelAttr || FORM_DROPDOWN_FIELD_DEFAULTS.label;

  const requiredAttr = getAttribute(element, 'required');
  const required = parseBoolean(requiredAttr, FORM_DROPDOWN_FIELD_DEFAULTS.required);

  const singleSelectAttr = getAttribute(element, 'singleSelect');
  const isSingleSelect = parseBoolean(
    singleSelectAttr,
    FORM_DROPDOWN_FIELD_DEFAULTS.isSingleSelect,
  );

  const helperTextAttr = getAttribute(element, 'helperText');
  const helpText = helperTextAttr || FORM_DROPDOWN_FIELD_DEFAULTS.helpText;
  const showHelpText = helperTextAttr ? true : FORM_DROPDOWN_FIELD_DEFAULTS.showHelpText;

  const showLabelAttr = getAttribute(element, 'showLabel');
  const showLabel = parseBoolean(showLabelAttr, FORM_DROPDOWN_FIELD_DEFAULTS.showLabel);

  const placeholderAttr = getAttribute(element, 'placeholder');
  const placeholder = placeholderAttr || FORM_DROPDOWN_FIELD_DEFAULTS.placeholder;

  // Parse options from child <option> elements
  const options = parseOptions(element);

  const props: Record<string, unknown> = {
    id: FORM_DROPDOWN_FIELD_DEFAULTS.id,
    label,
    showLabel,
    inputType: FORM_DROPDOWN_FIELD_DEFAULTS.inputType,
    required,
    hidden: FORM_DROPDOWN_FIELD_DEFAULTS.hidden,
    urlMapping: FORM_DROPDOWN_FIELD_DEFAULTS.urlMapping,
    placeholder,
    options,
    defaultOptions: FORM_DROPDOWN_FIELD_DEFAULTS.defaultOptions,
    isSingleSelect,
    helpText,
    showHelpText,
  };

  return {
    type: { resolvedName: FORM_DROPDOWN_FIELD_NODE_NAME.replace(/\s/g, '') },
    isCanvas: false,
    props,
    displayName: FORM_DROPDOWN_FIELD_NODE_NAME,
    custom: {
      displayName: 'Dropdown',
      isLocked: false,
    },
    hidden: false,
    nodes: [],
    linkedNodes: {},
    parent: context.parentId,
  };
};

/**
 * Transform a <select> XML element into a CraftJS FormSelectionField node
 *
 * @param element - The XML element
 * @param context - Conversion context
 * @returns SerializedNode for the FormSelectionField
 */
export const transformSelect = (element: Element, context: ConversionContext): SerializedNode => {
  const labelAttr = getAttribute(element, 'label');
  const label = labelAttr || FORM_SELECTION_FIELD_DEFAULTS.label;

  const requiredAttr = getAttribute(element, 'required');
  const required = parseBoolean(requiredAttr, FORM_SELECTION_FIELD_DEFAULTS.required);

  const singleSelectAttr = getAttribute(element, 'singleSelect');
  const isSingleSelect = parseBoolean(
    singleSelectAttr,
    FORM_SELECTION_FIELD_DEFAULTS.isSingleSelect,
  );

  const helperTextAttr = getAttribute(element, 'helperText');
  const helpText = helperTextAttr || FORM_SELECTION_FIELD_DEFAULTS.helpText;
  const showHelpText = helperTextAttr ? true : FORM_SELECTION_FIELD_DEFAULTS.showHelpText;

  const placeholderAttr = getAttribute(element, 'placeholder');
  const placeholder = placeholderAttr || FORM_SELECTION_FIELD_DEFAULTS.placeholder;

  // Parse options from child <option> elements
  const options = parseOptions(element);

  const props: Record<string, unknown> = {
    id: FORM_SELECTION_FIELD_DEFAULTS.id,
    label,
    inputType: FORM_SELECTION_FIELD_DEFAULTS.inputType,
    required,
    hidden: FORM_SELECTION_FIELD_DEFAULTS.hidden,
    placeholder,
    options,
    defaultOption: FORM_SELECTION_FIELD_DEFAULTS.defaultOptions,
    isSingleSelect,
    helpText,
    showHelpText,
  };

  return {
    type: { resolvedName: FORM_SELECTION_FIELD_NODE_NAME.replace(/\s/g, '') },
    isCanvas: false,
    props,
    displayName: FORM_SELECTION_FIELD_NODE_NAME,
    custom: {
      displayName: 'Selection',
      isLocked: false,
    },
    hidden: false,
    nodes: [],
    linkedNodes: {},
    parent: context.parentId,
  };
};

/**
 * Transform a <consent> XML element into a CraftJS FormConsentField node
 *
 * @param element - The XML element
 * @param context - Conversion context
 * @returns SerializedNode for the FormConsentField
 */
export const transformConsent = (element: Element, context: ConversionContext): SerializedNode => {
  const textAttr = getAttribute(element, 'text');
  const text = textAttr || FORM_CONSENT_FIELD_DEFAULTS.text;

  const requiredAttr = getAttribute(element, 'required');
  const required = parseBoolean(requiredAttr, FORM_CONSENT_FIELD_DEFAULTS.required);

  const descriptionAttr = getAttribute(element, 'description');
  const description = descriptionAttr || FORM_CONSENT_FIELD_DEFAULTS.description;
  const showDescription = !!descriptionAttr;

  const props: Record<string, unknown> = {
    id: FORM_CONSENT_FIELD_DEFAULTS.id,
    label: FORM_CONSENT_FIELD_DEFAULTS.label,
    inputType: FORM_CONSENT_FIELD_DEFAULTS.inputType,
    text,
    required,
    hidden: FORM_CONSENT_FIELD_DEFAULTS.hidden,
    selectedByDefault: FORM_CONSENT_FIELD_DEFAULTS.selectedByDefault,
    showDescription,
    description,
    showUrlLink: FORM_CONSENT_FIELD_DEFAULTS.showUrlLink,
    linkUrl: FORM_CONSENT_FIELD_DEFAULTS.linkUrl,
    linkText: FORM_CONSENT_FIELD_DEFAULTS.linkText,
    selectedLinkVariant: FORM_CONSENT_FIELD_DEFAULTS.selectedLinkVariant,
  };

  return {
    type: { resolvedName: FORM_CONSENT_FIELD_NODE_NAME.replace(/\s/g, '') },
    isCanvas: false,
    props,
    displayName: FORM_CONSENT_FIELD_NODE_NAME,
    custom: {
      displayName: 'Consent',
      isLocked: false,
    },
    hidden: false,
    nodes: [],
    linkedNodes: {},
    parent: context.parentId,
  };
};

/**
 * Transform a <submitButton> XML element into a CraftJS FormSubmitButton node
 *
 * @param element - The XML element
 * @param context - Conversion context
 * @returns SerializedNode for the FormSubmitButton
 */
export const transformSubmitButton = (
  element: Element,
  context: ConversionContext,
): SerializedNode => {
  const textAttr = getAttribute(element, 'text');
  const text = textAttr || FORM_SUBMIT_BUTTON_DEFAULTS.text;

  const variantAttr = getAttribute(element, 'variant');
  const variant = mapButtonVariant(variantAttr) || FORM_SUBMIT_BUTTON_DEFAULTS.variant;

  const sizeAttr = getAttribute(element, 'size');
  const size = mapButtonSize(sizeAttr) || FORM_SUBMIT_BUTTON_DEFAULTS.size;

  const fullWidthAttr = getAttribute(element, 'fullWidth');
  const fullWidth = parseBoolean(fullWidthAttr, FORM_SUBMIT_BUTTON_DEFAULTS.fullWidth);

  const props: Record<string, unknown> = {
    text,
    variant,
    size,
    fullWidth,
  };

  return {
    type: { resolvedName: FORM_SUBMIT_BUTTON_NODE_NAME.replace(/\s/g, '') },
    isCanvas: false,
    props,
    displayName: FORM_SUBMIT_BUTTON_NODE_NAME,
    custom: {
      displayName: 'Submit',
      isLocked: false,
    },
    hidden: false,
    nodes: [],
    linkedNodes: {},
    parent: context.parentId,
  };
};

/**
 * Map variant attribute to button variant
 */
const mapButtonVariant = (value: string | null): 'primary' | 'secondary' | 'accent' | undefined => {
  if (!value) return undefined;

  const normalized = value.toLowerCase().trim();
  switch (normalized) {
    case 'primary':
      return 'primary';
    case 'secondary':
      return 'secondary';
    case 'accent':
      return 'accent';
    default:
      return undefined;
  }
};

/**
 * Map size attribute to button size
 */
const mapButtonSize = (value: string | null): 'small' | 'medium' | 'large' | undefined => {
  if (!value) return undefined;

  const normalized = value.toLowerCase().trim();
  switch (normalized) {
    case 'small':
      return 'small';
    case 'medium':
      return 'medium';
    case 'large':
      return 'large';
    default:
      return undefined;
  }
};

/**
 * Valid attributes for shortText elements
 */
export const SHORT_TEXT_ATTRIBUTES = [
  'label',
  'placeholder',
  'required',
  'helperText',
  'showLabel',
] as const;

/**
 * Valid attributes for email elements
 */
export const EMAIL_ATTRIBUTES = [
  'label',
  'placeholder',
  'required',
  'helperText',
  'showLabel',
] as const;

/**
 * Valid attributes for phone elements
 */
export const PHONE_ATTRIBUTES = [
  'label',
  'placeholder',
  'required',
  'helperText',
  'showLabel',
] as const;

/**
 * Valid attributes for dropdown elements
 */
export const DROPDOWN_ATTRIBUTES = [
  'label',
  'required',
  'singleSelect',
  'helperText',
  'showLabel',
  'placeholder',
] as const;

/**
 * Valid attributes for select elements
 */
export const SELECT_ATTRIBUTES = [
  'label',
  'required',
  'singleSelect',
  'helperText',
  'placeholder',
] as const;

/**
 * Valid attributes for consent elements
 */
export const CONSENT_ATTRIBUTES = ['text', 'required', 'description'] as const;

/**
 * Valid attributes for submitButton elements
 */
export const SUBMIT_BUTTON_ATTRIBUTES = ['text', 'variant', 'size', 'fullWidth'] as const;

/**
 * Check if an attribute is valid for shortText elements
 */
export const isValidShortTextAttribute = (attr: string): boolean => {
  return (SHORT_TEXT_ATTRIBUTES as readonly string[]).includes(attr);
};

/**
 * Check if an attribute is valid for email elements
 */
export const isValidEmailAttribute = (attr: string): boolean => {
  return (EMAIL_ATTRIBUTES as readonly string[]).includes(attr);
};

/**
 * Check if an attribute is valid for phone elements
 */
export const isValidPhoneAttribute = (attr: string): boolean => {
  return (PHONE_ATTRIBUTES as readonly string[]).includes(attr);
};

/**
 * Check if an attribute is valid for dropdown elements
 */
export const isValidDropdownAttribute = (attr: string): boolean => {
  return (DROPDOWN_ATTRIBUTES as readonly string[]).includes(attr);
};

/**
 * Check if an attribute is valid for select elements
 */
export const isValidSelectAttribute = (attr: string): boolean => {
  return (SELECT_ATTRIBUTES as readonly string[]).includes(attr);
};

/**
 * Check if an attribute is valid for consent elements
 */
export const isValidConsentAttribute = (attr: string): boolean => {
  return (CONSENT_ATTRIBUTES as readonly string[]).includes(attr);
};

/**
 * Check if an attribute is valid for submitButton elements
 */
export const isValidSubmitButtonAttribute = (attr: string): boolean => {
  return (SUBMIT_BUTTON_ATTRIBUTES as readonly string[]).includes(attr);
};

/**
 * Form field types for validation
 */
export type FormFieldType =
  | 'shortText'
  | 'email'
  | 'phone'
  | 'dropdown'
  | 'select'
  | 'consent'
  | 'submitButton';

/**
 * Get the validator function for a form field type
 */
export const getFormFieldValidator = (type: FormFieldType): ((attr: string) => boolean) => {
  switch (type) {
    case 'shortText':
      return isValidShortTextAttribute;
    case 'email':
      return isValidEmailAttribute;
    case 'phone':
      return isValidPhoneAttribute;
    case 'dropdown':
      return isValidDropdownAttribute;
    case 'select':
      return isValidSelectAttribute;
    case 'consent':
      return isValidConsentAttribute;
    case 'submitButton':
      return isValidSubmitButtonAttribute;
  }
};

/**
 * Get the transform function for a form field type
 */
export const getFormFieldTransform = (
  type: FormFieldType,
): ((element: Element, context: ConversionContext) => SerializedNode) => {
  switch (type) {
    case 'shortText':
      return transformShortText;
    case 'email':
      return transformEmail;
    case 'phone':
      return transformPhone;
    case 'dropdown':
      return transformDropdown;
    case 'select':
      return transformSelect;
    case 'consent':
      return transformConsent;
    case 'submitButton':
      return transformSubmitButton;
  }
};
